// src/components/admin/Users.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · User Management
//  Same "TelcoFair Glass Workspace" system as the Dashboard: cool workspace canvas, TelcoFair blue
//  accents, frosted panes with specular rim-light and pointer-tracked sheen.
//  Layout is pure CSS Grid — no MUI <Grid> anywhere.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Avatar,
    IconButton,
    Chip,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    LinearProgress,
    Skeleton,
    Tabs,
    Tab,
    CircularProgress,
    Fade,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    People as PeopleIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Save as SaveIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Verified as VerifiedIcon,
    DoNotDisturb as DoNotDisturbIcon,
    Shield as ShieldIcon,
    Groups as GroupsIcon,
    FiberManualRecord as LiveIcon,
    Business as BusinessIcon,
    CalendarToday as CalendarIcon,
    Close as CloseIcon,
    PhotoCamera as PhotoCameraIcon,
    Event as EventIcon,
    FilterAltOff as FilterOffIcon,
    WarningAmber as WarningIcon,
    BadgeOutlined as BadgeIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db, auth } from '../../config/firebase';
import {
    doc,
    getDocs,
    collection,
    updateDoc,
    setDoc,
    serverTimestamp,
} from 'firebase/firestore';
import { initializeApp, getApps, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, sendEmailVerification, signOut, deleteUser } from 'firebase/auth';
import toast from 'react-hot-toast';
import { uploadAdminFile, validateAdminUpload } from '../../services/firebase/storage';
import { getPlatformIntegrationSettings, manualVerifyUserEmail, sendProfessionalVerificationEmail } from '../../services/api/platformIntegrationService';
import { friendlyError } from '../../utils/userMessages';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS  (identical to Dashboard.jsx — extract to
       src/theme/liquid.js once you've rolled this across the app)
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkFaint: '#AAB6C1',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

const glassField = (accent = palette.rust) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        fontSize: '0.875rem',
        background: rgba('#FFFFFF', 0.6),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow .3s ease',
        '& fieldset': { borderColor: palette.hairline, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: rgba(accent, 0.4) },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${rgba(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        try { return value.toDate(); } catch { return null; }
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatDate = (value) => {
    const d = toDate(value);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const initialsOf = (user) => {
    const source = user?.displayName || user?.email || '';
    const parts = String(source).trim().split(/[\s@._-]+/).filter(Boolean);
    if (!parts.length) return 'U';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

const ROLES = [
    { value: 'super_admin', label: 'Super Admin', accent: palette.rust },
    { value: 'admin', label: 'Admin', accent: palette.plum },
    { value: 'moderator', label: 'Moderator', accent: palette.amber },
    { value: 'sponsor', label: 'Sponsor', accent: palette.teal },
    { value: 'speaker', label: 'Speaker', accent: palette.indigo },
    { value: 'attendee', label: 'Attendee', accent: palette.pine },
];

const roleMeta = (role) =>
    ROLES.find((r) => r.value === role) || { value: role || 'attendee', label: role || 'Attendee', accent: palette.inkLight };

const statusOf = (user) => {
    if (user?.isBlocked) return 'blocked';
    if (user?.isActive === false) return 'inactive';
    if (user?.isVerified === false) return 'unverified';
    return 'active';
};

const STATUS_META = {
    active: { label: 'Active', tone: palette.success },
    blocked: { label: 'Blocked', tone: palette.danger },
    inactive: { label: 'Inactive', tone: palette.inkLight },
    unverified: { label: 'Unverified', tone: palette.warning },
};

const useCountUp = (target, duration = 900) => {
    const [display, setDisplay] = useState(0);
    const fromRef = useRef(0);

    useEffect(() => {
        const to = Number(target) || 0;
        const from = fromRef.current;
        if (from === to) { setDisplay(to); return undefined; }

        let raf;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const current = from + (to - from) * eased;
            fromRef.current = current;
            setDisplay(current);
            if (p < 1) raf = requestAnimationFrame(tick);
            else fromRef.current = to;
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return display;
};

/* Creating a user with the primary Auth instance silently signs the ADMIN out
   and into the new account. We provision on a throwaway secondary app instead,
   so the admin session is never touched. */
const SECONDARY_APP = 'tf-user-provisioning';

const provisionAuthUser = async (email, password, { sendFirebaseVerification = false, persistProfile } = {}) => {
    let secondary = null;
    let createdUser = null;
    try {
        secondary =
            getApps().find((a) => a.name === SECONDARY_APP) ||
            initializeApp(auth.app.options, SECONDARY_APP);
        const secondaryAuth = getAuth(secondary);
        const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
        createdUser = cred.user;

        // The Firestore user profile is part of provisioning, not an optional
        // follow-up. Persist it before any verification delivery so a mail
        // failure can never produce an Auth-only account.
        if (typeof persistProfile === 'function') await persistProfile(createdUser.uid);

        let verificationWarning = '';
        if (sendFirebaseVerification) {
            try { await sendEmailVerification(createdUser); }
            catch (verificationError) { verificationWarning = friendlyError(verificationError, 'User was created, but the Firebase verification email could not be sent.'); }
        }
        await signOut(secondaryAuth);
        return { uid: createdUser.uid, verificationWarning };
    } catch (error) {
        // If the profile could not be persisted, roll the newly-created Auth
        // identity back while the secondary user session is still available.
        // If only verification delivery failed after persistence, keep the
        // account so verification can safely be resent from the Users screen.
        if (createdUser && error?.__profilePersistenceFailed) {
            await deleteUser(createdUser).catch(() => {});
        }
        throw error;
    } finally {
        if (secondary) {
            try { await deleteApp(secondary); } catch (error) { console.debug('[secondary auth cleanup]', error); }
        }
    }
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 16 }, (_, i) => ({
                id: i,
                size: 3 + ((i * 5) % 8),
                x: (i * 41) % 100,
                y: (i * 59) % 100,
                duration: 28 + ((i * 4) % 16),
                delay: (i * 1.9) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box component="svg" sx={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
                <defs>
                    <filter id="tf-users-liquid">
                        <feTurbulence type="fractalNoise" baseFrequency="0.009 0.013" numOctaves="2" seed="3" result="noise">
                            <animate attributeName="baseFrequency" dur="32s" values="0.009 0.013;0.015 0.007;0.009 0.013" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="66" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            <motion.div
                animate={{ x: [0, -60, 40, 0], y: [0, 40, -30, 0], scale: [1, 1.12, 0.92, 1] }}
                transition={{ duration: 31, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-8%', left: '-5%',
                    width: 600, height: 600, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, ${rgba(palette.rustGlow, 0.3)}, ${rgba(palette.rust, 0.12)} 46%, transparent 72%)`,
                    filter: 'url(#tf-users-liquid) blur(44px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 60, -35, 0], y: [0, -40, 30, 0], scale: [1, 0.9, 1.14, 1] }}
                transition={{ duration: 37, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-12%', right: '-6%',
                    width: 560, height: 560, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, ${rgba(palette.plumGlow, 0.22)}, ${rgba(palette.teal, 0.12)} 48%, transparent 72%)`,
                    filter: 'url(#tf-users-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-56%', '-45%', '-50%'], y: ['-50%', '-45%', '-56%', '-50%'], scale: [1, 1.07, 0.95, 1] }}
                transition={{ duration: 27, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '42%', left: '52%',
                    width: 720, height: 720, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.15)}, transparent 66%)`,
                    filter: 'blur(80px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 40}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 12}%`, `${m.x}%`],
                        opacity: [0, 0.45, 0],
                        scale: [0.4, 1, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.5,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.022)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.022)} 1px, transparent 1px)`,
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

const Rule = ({ color = palette.rust, w = 44 }) => (
    <Box
        sx={{
            width: w, height: 3, mt: 0.75, borderRadius: 3,
            background: `linear-gradient(90deg, ${color}, ${rgba(color, 0)})`,
            animation: 'tfRule 3.4s ease-in-out infinite',
        }}
    />
);

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = false,
    padding = { xs: 2.25, md: 2.75 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    ...(lift && {
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 26px 56px -18px ${rgba(accent, 0.34)}`,
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const PanelHead = ({ icon, title, subtitle, accent = palette.rust, chip, right, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.5, flexWrap: 'wrap', mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay, fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: '0.76rem', color: palette.inkLight, mt: 0.25 }}>{subtitle}</Typography>
                ) : (
                    <Rule color={accent} w={dense ? 34 : 44} />
                )}
            </Box>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: accent, bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                    }}
                />
            )}
        </Box>
        {right}
    </Box>
);

const EmptyState = ({ icon, title, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1.5 }}>
            <GlassIcon icon={icon} color={palette.inkFaint} size={54} />
        </Box>
        {title && (
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.05rem', color: palette.ink }}>
                {title}
            </Typography>
        )}
        <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', mt: 0.5 }}>{text}</Typography>
        {action && (
            <Button
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 2.25, textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.5, py: 1,
                    color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

const StatCard = ({ label, value, icon, accent, delta, hint, delay = 0, loading }) => {
    const animated = useCountUp(value);
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const trendColor =
        delta?.trend === 'up' ? palette.success : delta?.trend === 'down' ? palette.danger : palette.inkLight;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 24 }),
                    height: '100%', p: { xs: 2, md: 2.4 },
                    display: 'flex', flexDirection: 'column', minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-7px) scale(1.012)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 28px 60px -20px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute', top: -70, right: -70, width: 170, height: 170,
                        borderRadius: '50%', filter: 'blur(38px)', opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={40} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={delta.trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> : <TrendingUpIcon sx={{ fontSize: 13 }} />}
                                label={delta.label}
                                sx={{
                                    height: 22, fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                    color: trendColor, bgcolor: rgba(trendColor, 0.09),
                                    border: `1px solid ${rgba(trendColor, 0.18)}`,
                                    '& .MuiChip-icon': { color: trendColor, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay, fontWeight: 600,
                                fontSize: { xs: '1.85rem', md: '2.2rem' },
                                lineHeight: 1, letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {compact(animated)}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.12em', color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography sx={{ mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                            component="span"
                            sx={{
                                width: 6, height: 6, borderRadius: '50%', bgcolor: accent,
                                boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                animation: 'tfPulse 2.4s ease-in-out infinite',
                            }}
                        />
                        {hint}
                    </Typography>
                </Box>
            </Paper>
        </motion.div>
    );
};

const RoleBadge = ({ role }) => {
    const meta = roleMeta(role);
    return (
        <Box
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                px: 1, py: 0.35, borderRadius: '9px',
                fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
                letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                color: meta.accent, bgcolor: rgba(meta.accent, 0.1),
                border: `1px solid ${rgba(meta.accent, 0.2)}`,
            }}
        >
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: meta.accent }} />
            {meta.label}
        </Box>
    );
};

const StatusPill = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.inactive;
    return (
        <Box
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.35, borderRadius: '9px',
                fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
                letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                color: meta.tone, bgcolor: rgba(meta.tone, 0.1),
                border: `1px solid ${rgba(meta.tone, 0.2)}`,
            }}
        >
            <Box
                sx={{
                    width: 5, height: 5, borderRadius: '50%', bgcolor: meta.tone,
                    animation: status === 'active' ? 'tfPulse 2.4s ease-in-out infinite' : 'none',
                }}
            />
            {meta.label}
        </Box>
    );
};

const UserAvatar = ({ user, size = 40, radius = 14 }) => {
    const accent = user?.isBlocked ? palette.inkLight : roleMeta(user?.role).accent;
    return (
        <Avatar
            src={user?.profilePicture || user?.photoURL || ''}
            sx={{
                width: size, height: size, borderRadius: `${radius}px`, flexShrink: 0,
                fontFamily: fontMono, fontSize: size * 0.32, fontWeight: 700, color: '#fff',
                background: `linear-gradient(150deg, ${accent}, ${rgba(accent, 0.7)})`,
                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.4)}, 0 8px 18px -8px ${rgba(accent, 0.85)}`,
                filter: user?.isBlocked ? 'grayscale(0.6)' : 'none',
            }}
        >
            {initialsOf(user)}
        </Avatar>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   5 · CONFIRM DIALOG (shared by delete + block/unblock)
   ═══════════════════════════════════════════════════════════════════════════ */

const ConfirmDialog = ({ open, onClose, onConfirm, tone, icon, title, body, confirmLabel, busy }) => (
    <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ paper: {
            sx: {
                ...liquid(tone, { radius: 26 }),
                background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(tone, 0.1)})`,
            },
        } }}
    >
        <Box sx={{ p: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'grid', placeItems: 'center', mb: 2 }}>
                <GlassIcon icon={icon} color={tone} size={54} />
            </Box>
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                {title}
            </Typography>
            <Typography sx={{ color: palette.inkSoft, fontSize: '0.86rem', mt: 1, mb: 2.75 }}>
                {body}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.25 }}>
                <Button
                    fullWidth
                    onClick={onClose}
                    disabled={busy}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: palette.inkSoft }}
                >
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={onConfirm}
                    disabled={busy}
                    startIcon={busy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : icon}
                    sx={{
                        borderRadius: '12px', textTransform: 'none', fontWeight: 700, color: '#fff',
                        background: `linear-gradient(140deg, ${tone}, ${rgba(tone, 0.75)})`,
                        boxShadow: `0 12px 26px -12px ${rgba(tone, 1)}`,
                        '&.Mui-disabled': { color: rgba('#FFFFFF', 0.7) },
                    }}
                >
                    {busy ? 'Working…' : confirmLabel}
                </Button>
            </Box>
        </Box>
    </Dialog>
);

/* ═══════════════════════════════════════════════════════════════════════════
   6 · USER DETAIL DIALOG
   ═══════════════════════════════════════════════════════════════════════════ */

const DetailLine = ({ icon, label, value, accent }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.85 }}>
        <Box
            sx={{
                width: 28, height: 28, borderRadius: '9px', display: 'grid', placeItems: 'center', flexShrink: 0,
                color: accent, bgcolor: rgba(accent, 0.1), border: `1px solid ${rgba(accent, 0.16)}`,
                '& svg': { fontSize: 15 },
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint }}>
                {label}
            </Typography>
            <Box sx={{ fontSize: '0.84rem', color: palette.ink, fontWeight: 500, overflowWrap: 'anywhere' }}>
                {value}
            </Box>
        </Box>
    </Box>
);

const UserDetailDialog = ({ user, open, onClose, onEdit, isSelf }) => {
    const [tab, setTab] = useState(0);

    useEffect(() => { if (open) setTab(0); }, [open]);

    if (!user) return null;
    const accent = roleMeta(user.role).accent;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            TransitionComponent={Fade}
            slotProps={{ paper: {
                sx: {
                    ...liquid(accent, { radius: 28 }),
                    background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(accent, 0.1)})`,
                    maxHeight: '92vh',
                },
            } }}
        >
            <Box
                sx={{
                    p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${palette.hairlineSoft}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    <UserAvatar user={user} size={64} radius={20} />
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                                sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.35rem', color: palette.ink, letterSpacing: '-0.025em' }}
                                noWrap
                            >
                                {user.displayName || user.email?.split('@')[0] || 'Unknown user'}
                            </Typography>
                            {user.isVerified && <VerifiedIcon sx={{ fontSize: 19, color: palette.pine }} />}
                            {isSelf && (
                                <Box
                                    sx={{
                                        px: 0.9, py: 0.25, borderRadius: '8px', fontFamily: fontMono,
                                        fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase',
                                        color: palette.rust, bgcolor: rgba(palette.rust, 0.1),
                                        border: `1px solid ${rgba(palette.rust, 0.2)}`,
                                    }}
                                >
                                    You
                                </Box>
                            )}
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', color: palette.inkLight }} noWrap>
                            {user.email}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.9, flexWrap: 'wrap' }}>
                            <RoleBadge role={user.role} />
                            <StatusPill status={statusOf(user)} />
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        startIcon={<EditIcon sx={{ fontSize: 17 }} />}
                        onClick={() => { onClose(); onEdit(user); }}
                        sx={{
                            textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 2,
                            color: palette.amber, border: `1px solid ${rgba(palette.amber, 0.35)}`,
                            background: rgba(palette.amber, 0.06),
                            '&:hover': { background: rgba(palette.amber, 0.12) },
                        }}
                    >
                        Edit
                    </Button>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                            transition: 'transform .3s ease, background .3s ease',
                            '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ p: 2.75 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        minHeight: 40, mb: 2.5,
                        '& .MuiTab-root': {
                            minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
                            fontFamily: fontBody, color: palette.inkLight,
                            '&.Mui-selected': { color: palette.ink },
                        },
                        '& .MuiTabs-indicator': { height: 3, borderRadius: 3, backgroundColor: accent },
                    }}
                >
                    <Tab icon={<PersonIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Profile" />
                    <Tab icon={<EventIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Activity" />
                </Tabs>

                {tab === 0 ? (
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                        <GlassPanel accent={palette.rust} delay={0}>
                            <PanelHead icon={<BadgeIcon />} title="Personal" accent={palette.rust} dense />
                            <DetailLine icon={<PersonIcon />} label="Display name" accent={palette.rust} value={user.displayName || 'Not set'} />
                            <DetailLine icon={<EmailIcon />} label="Email" accent={palette.rust} value={user.email || 'Not set'} />
                            <DetailLine icon={<PhoneIcon />} label="Phone" accent={palette.rust} value={user.phoneNumber || 'Not set'} />
                            <DetailLine icon={<CalendarIcon />} label="Joined" accent={palette.rust} value={formatDate(user.createdAt)} />
                        </GlassPanel>

                        <GlassPanel accent={palette.pine} delay={0.06}>
                            <PanelHead icon={<ShieldIcon />} title="Account" accent={palette.pine} dense />
                            <DetailLine icon={<ShieldIcon />} label="Role" accent={palette.pine} value={<RoleBadge role={user.role} />} />
                            <DetailLine icon={<VerifiedIcon />} label="Verification" accent={palette.pine} value={user.isVerified ? 'Verified' : 'Unverified'} />
                            <DetailLine icon={<CheckCircleIcon />} label="Status" accent={palette.pine} value={<StatusPill status={statusOf(user)} />} />
                            <DetailLine icon={<BusinessIcon />} label="Company" accent={palette.pine} value={user.companyName || 'Unaffiliated'} />
                            <DetailLine icon={<CalendarIcon />} label="Last updated" accent={palette.pine} value={formatDate(user.updatedAt)} />
                        </GlassPanel>
                    </Box>
                ) : (
                    <EmptyState icon={<EventIcon />} text="Activity history isn't wired up yet" />
                )}
            </DialogContent>
        </Dialog>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   7 · ADD / EDIT USER DIALOG
   Local state — typing here never re-renders the users table.
   ═══════════════════════════════════════════════════════════════════════════ */

const EMPTY_FORM = {
    email: '',
    displayName: '',
    password: '',
    phoneNumber: '',
    role: 'attendee',
    companyId: '',
    companyName: '',
    isActive: true,
    isVerified: false,
    profilePicture: '',
};

const UserFormDialog = ({ open, mode, user, companies, onClose, onSaved }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showPassword, setShowPassword] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState(false);

    const isEdit = mode === 'edit';

    useEffect(() => {
        if (!open) return;
        setTouched(false);
        setShowPassword(false);
        setUploadPct(0);
        setForm(
            isEdit && user
                ? {
                      email: user.email || '',
                      displayName: user.displayName || '',
                      password: '',
                      phoneNumber: user.phoneNumber || '',
                      role: user.role || 'attendee',
                      companyId: user.companyId || '',
                      companyName: user.companyName || '',
                      isActive: user.isActive !== false,
                      isVerified: user.isVerified === true || user.emailVerified === true,
                      profilePicture: user.profilePicture || user.photoURL || '',
                  }
                : EMPTY_FORM
        );
    }, [open, isEdit, user]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const emailError = !isEdit && touched && !EMAIL_RE.test(form.email.trim()) ? 'Enter a valid email address' : '';
    const passwordError = !isEdit && touched && form.password.length < 8 ? 'At least 8 characters' : '';
    const valid = isEdit || (EMAIL_RE.test(form.email.trim()) && form.password.length >= 8);

    const handleAvatar = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Choose an image file'); return; }
        if (file.size > MAX_AVATAR_BYTES) { toast.error('Image must be under 2 MB'); return; }

        setUploading(true);
        setUploadPct(0);
        (async () => {
            try {
                validateAdminUpload(file, { kind: 'image', maxBytes: MAX_AVATAR_BYTES });
                const targetUserId = isEdit && user?.id ? user.id : '';
                const asset = await uploadAdminFile({
                    file,
                    scope: targetUserId ? 'user' : 'platform',
                    userId: targetUserId || undefined,
                    folder: targetUserId ? 'profile-photos' : 'user-profile-drafts',
                    kind: 'image',
                    maxBytes: MAX_AVATAR_BYTES,
                    onProgress: setUploadPct,
                });
                set({ profilePicture: asset.url });
                toast.success('Photo uploaded');
            } catch (err) {
                console.error('[users] avatar upload failed:', err);
                toast.error(friendlyError(err, 'Unable to upload this photo.'));
            } finally {
                setUploading(false);
                setUploadPct(0);
            }
        })();
    };

    const handleSave = async () => {
        setTouched(true);
        if (!valid || saving || uploading) {
            if (uploading) toast.error('Wait for the photo upload to finish');
            else if (!valid) toast.error('Fix the highlighted fields first');
            return;
        }

        setSaving(true);
        try {
            const integrationSettings = await getPlatformIntegrationSettings().catch(() => ({ settings: {} }));
            const verificationProvider = integrationSettings?.settings?.auth?.verificationProvider || 'firebase_auth';
            const verificationChanged = isEdit && Boolean(form.isVerified) !== Boolean(user?.isVerified || user?.emailVerified);
            if (verificationProvider === 'firebase_auth' && ((isEdit && verificationChanged) || (!isEdit && form.isVerified))) {
                throw new Error('Firebase email verification cannot be manually overridden from the browser. Leave the account unverified and let the user complete the Firebase verification email.');
            }
            const shared = {
                displayName: form.displayName.trim() || form.email.trim().split('@')[0],
                phoneNumber: form.phoneNumber.trim(),
                role: form.role,
                isActive: form.isActive,
                isVerified: form.isVerified,
                companyId: form.companyId || '',
                companyName: form.companyName || '',
                profilePicture: form.profilePicture || '',
                updatedAt: serverTimestamp(),
            };

            if (isEdit) {
                await updateDoc(doc(db, 'users', user.id), shared);
                if (verificationChanged) {
                    await manualVerifyUserEmail(user.id, Boolean(form.isVerified));
                }
                toast.success('User updated');
            } else {
                // Provision on a secondary app so the platform admin stays signed in.
                // Firebase Auth stores the account; configured verification delivery handles activation.
                const normalizedEmail = form.email.trim().toLowerCase();
                const provisioned = await provisionAuthUser(normalizedEmail, form.password, {
                    sendFirebaseVerification: verificationProvider === 'firebase_auth' && !form.isVerified,
                    persistProfile: async (createdUid) => {
                        try {
                            await setDoc(doc(db, 'users', createdUid), {
                                ...shared,
                                userId: createdUid,
                                uid: createdUid,
                                email: normalizedEmail,
                                emailNormalized: normalizedEmail,
                                emailVerified: false,
                                verificationEmailManaged: true,
                                isBlocked: false,
                                createdAt: serverTimestamp(),
                            });
                        } catch (profileError) {
                            profileError.__profilePersistenceFailed = true;
                            throw profileError;
                        }
                    },
                });
                const uid = provisioned.uid;
                let verificationWarning = provisioned.verificationWarning || '';
                if (form.isVerified) {
                    await manualVerifyUserEmail(uid, true);
                    toast.success('User created and email manually verified');
                } else if (verificationProvider === 'firebase_auth') {
                    if (verificationWarning) toast.error(verificationWarning, { duration: 6000 });
                    else toast.success('User created — Firebase verification email sent.');
                } else if (verificationProvider === 'manual') {
                    toast.success('User created — manual verification is required.');
                } else {
                    try {
                        await sendProfessionalVerificationEmail(uid);
                        toast.success(verificationProvider === 'whatsapp' ? 'User created — WhatsApp verification requested.' : 'User created — professional verification email requested.');
                    } catch (verificationError) {
                        verificationWarning = friendlyError(verificationError, 'User was created, but verification delivery failed. You can resend it from the Users page.');
                        toast.error(verificationWarning, { duration: 6000 });
                    }
                }
            }

            onSaved();
            onClose();
        } catch (err) {
            console.error('[users] save failed:', err);
            const map = {
                'auth/email-already-in-use': 'That email is already registered',
                'auth/invalid-email': 'That email address is invalid',
                'auth/weak-password': 'Password is too weak',
                'permission-denied': 'You do not have permission to do that',
            };
            toast.error(map[err.code] || 'Unable to save this user. Please check the details and try again.');
        } finally {
            setSaving(false);
        }
    };

    const accent = isEdit ? palette.amber : palette.rust;

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Fade}
            slotProps={{ paper: {
                sx: {
                    ...liquid(accent, { radius: 28 }),
                    background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(accent, 0.1)})`,
                    maxHeight: '92vh',
                },
            } }}
        >
            <Box
                sx={{
                    p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: `1px solid ${palette.hairlineSoft}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <GlassIcon icon={isEdit ? <EditIcon /> : <AddIcon />} color={accent} size={46} />
                    <Box>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink, letterSpacing: '-0.02em' }}>
                            {isEdit ? 'Edit user' : 'Add new user'}
                        </Typography>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                            {isEdit ? 'Update profile, role and access' : 'Creates an auth account and profile record'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft,
                        bgcolor: rgba(palette.ink, 0.04),
                        transition: 'transform .3s ease, background .3s ease',
                        '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* avatar */}
                <Box
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 2, p: 1.75, borderRadius: '18px',
                        background: rgba('#FFFFFF', 0.5), border: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <UserAvatar user={{ ...form, photoURL: form.profilePicture }} size={64} radius={20} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Button
                            component="label"
                            size="small"
                            disabled={uploading}
                            startIcon={uploading ? <CircularProgress size={14} sx={{ color: accent }} /> : <PhotoCameraIcon sx={{ fontSize: 17 }} />}
                            sx={{
                                textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 1.75,
                                color: accent, border: `1px solid ${rgba(accent, 0.35)}`, background: rgba(accent, 0.06),
                                '&:hover': { background: rgba(accent, 0.12) },
                            }}
                        >
                            {uploading ? `Uploading ${uploadPct}%` : 'Upload photo'}
                            <input type="file" accept="image/*" hidden onChange={handleAvatar} />
                        </Button>
                        <Typography sx={{ mt: 0.6, fontFamily: fontMono, fontSize: '0.62rem', color: palette.inkFaint }}>
                            Square image · max 2 MB
                        </Typography>
                        {uploading && (
                            <LinearProgress
                                variant="determinate"
                                value={uploadPct}
                                sx={{
                                    mt: 0.9, height: 4, borderRadius: 4, bgcolor: rgba(accent, 0.12),
                                    '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: accent },
                                }}
                            />
                        )}
                    </Box>
                    {form.profilePicture && (
                        <IconButton
                            size="small"
                            onClick={() => set({ profilePicture: '' })}
                            sx={{ color: palette.danger, bgcolor: rgba(palette.danger, 0.08), borderRadius: '10px' }}
                        >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                </Box>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                    <TextField
                        fullWidth size="small" label="Email address" type="email"
                        value={form.email}
                        onChange={(e) => set({ email: e.target.value })}
                        disabled={isEdit}
                        required={!isEdit}
                        error={Boolean(emailError)}
                        helperText={emailError || (isEdit ? 'Email cannot be changed here' : ' ')}
                        sx={glassField(accent)}
                    />
                    <TextField
                        fullWidth size="small" label="Display name"
                        value={form.displayName}
                        onChange={(e) => set({ displayName: e.target.value })}
                        helperText=" "
                        sx={glassField(accent)}
                    />

                    {!isEdit && (
                        <TextField
                            fullWidth size="small" label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={(e) => set({ password: e.target.value })}
                            required
                            error={Boolean(passwordError)}
                            helperText={passwordError || 'Minimum 6 characters'}
                            slotProps={{ input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setShowPassword((s) => !s)} edge="end">
                                            {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            } }}
                            sx={glassField(accent)}
                        />
                    )}

                    <TextField
                        fullWidth size="small" label="Phone number" placeholder="+91 98765 43210"
                        value={form.phoneNumber}
                        onChange={(e) => set({ phoneNumber: e.target.value })}
                        helperText=" "
                        sx={glassField(accent)}
                    />

                    <FormControl fullWidth size="small" sx={glassField(accent)}>
                        <InputLabel>Role</InputLabel>
                        <Select value={form.role} label="Role" onChange={(e) => set({ role: e.target.value })}>
                            {ROLES.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.accent }} />
                                        {r.label}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" sx={glassField(accent)}>
                        <InputLabel>Company</InputLabel>
                        <Select
                            value={form.companyId}
                            label="Company"
                            onChange={(e) => {
                                const match = companies.find((c) => c.id === e.target.value);
                                set({
                                    companyId: e.target.value,
                                    companyName: match?.companyName || match?.name || '',
                                });
                            }}
                        >
                            <MenuItem value="">No company</MenuItem>
                            {companies.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                    {c.companyName || c.name || 'Unnamed company'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {[
                        { key: 'isActive', label: 'Active', tone: palette.pine },
                        { key: 'isVerified', label: 'Verified', tone: palette.plum },
                    ].map((t) => (
                        <FormControlLabel
                            key={t.key}
                            control={
                                <Switch
                                    checked={form[t.key]}
                                    onChange={(e) => set({ [t.key]: e.target.checked })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: t.tone },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: t.tone },
                                    }}
                                />
                            }
                            label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>{t.label}</Typography>}
                        />
                    ))}
                </Box>

                {!isEdit && (
                    <Box
                        sx={{
                            display: 'flex', gap: 1.25, p: 1.5, borderRadius: '14px',
                            background: rgba(palette.warning, 0.07),
                            border: `1px solid ${rgba(palette.warning, 0.2)}`,
                        }}
                    >
                        <WarningIcon sx={{ fontSize: 18, color: palette.warning, flexShrink: 0, mt: 0.2 }} />
                        <Typography sx={{ fontSize: '0.76rem', color: palette.inkSoft, lineHeight: 1.5 }}>
                            The account is provisioned on a temporary auth instance, so your admin session stays intact.
                            Share the password securely — it is never stored in Firestore.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <Box
                sx={{
                    px: 2.75, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25,
                    borderTop: `1px solid ${palette.hairlineSoft}`, background: rgba('#FFFFFF', 0.45), backdropFilter: 'blur(8px)',
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={saving}
                    sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '12px', px: 2 }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    disabled={saving || uploading}
                    startIcon={
                        saving ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                            : isEdit ? <SaveIcon sx={{ fontSize: 17 }} /> : <AddIcon sx={{ fontSize: 17 }} />
                    }
                    sx={{
                        textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                        background: `linear-gradient(140deg, ${accent}, ${rgba(accent, 0.72)})`,
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(accent, 0.95)}`,
                        '&.Mui-disabled': { background: rgba(palette.ink, 0.08), color: palette.inkFaint, boxShadow: 'none' },
                    }}
                >
                    {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
                </Button>
            </Box>
        </Dialog>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   8 · MAIN
   ═══════════════════════════════════════════════════════════════════════════ */

const Users = () => {
    // AuthContext exposes the account as `currentUser` in most of the app, but a
    // few providers expose `user` — accept either so the self-guards actually fire.
    const authValue = useAuth() || {};
    const account = authValue.currentUser || authValue.user || null;
    const myUid = account?.uid || authValue.userData?.userId || null;

    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formState, setFormState] = useState({ open: false, mode: 'add', user: null });
    const [detail, setDetail] = useState({ open: false, user: null });
    const [confirm, setConfirm] = useState({ open: false, kind: null, user: null });
    const [busy, setBusy] = useState(false);

    /* ── data ──────────────────────────────────────────────────────────────── */

    const loadUsers = useCallback(async (silent = false) => {
        if (silent) setRefreshing(true); else setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'users'));
            const list = [];
            snap.forEach((d) => {
                if (d.id.startsWith('_')) return; // schema/placeholder docs
                list.push({ id: d.id, ...d.data() });
            });
            // sorted client-side: an orderBy() query silently drops docs that
            // are missing the sort field, which would hide real users.
            list.sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));
            setUsers(list);
        } catch (err) {
            console.error('[users] load failed:', err);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    const loadCompanies = useCallback(async () => {
        try {
            const snap = await getDocs(collection(db, 'companies'));
            const list = [];
            snap.forEach((d) => {
                if (d.id.startsWith('_')) return;
                const data = d.data() || {};
                list.push({ id: d.id, ...data, companyName: data.companyName || data.name || 'Unnamed company' });
            });
            list.sort((a, b) => a.companyName.localeCompare(b.companyName));
            setCompanies(list);
        } catch (err) {
            console.warn('[companies] load failed:', err.message);
        }
    }, []);

    useEffect(() => {
        loadUsers();
        loadCompanies();
    }, [loadUsers, loadCompanies]);

    /* ── derived: filtering is computed, not stored (no stale filter state) ── */

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return users.filter((u) => {
            if (roleFilter && u.role !== roleFilter) return false;
            if (statusFilter && statusOf(u) !== statusFilter) return false;
            if (!term) return true;
            return (
                u.displayName?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term) ||
                u.companyName?.toLowerCase().includes(term) ||
                String(u.phoneNumber || '').includes(term)
            );
        });
    }, [users, search, roleFilter, statusFilter]);

    // keep the page in range whenever the result set shrinks
    useEffect(() => {
        const maxPage = Math.max(Math.ceil(filtered.length / rowsPerPage) - 1, 0);
        if (page > maxPage) setPage(maxPage);
    }, [filtered.length, rowsPerPage, page]);

    const paginated = useMemo(
        () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filtered, page, rowsPerPage]
    );

    const stats = useMemo(() => {
        const since = Date.now() - 30 * 86400000;
        return {
            total: users.length,
            active: users.filter((u) => statusOf(u) === 'active').length,
            blocked: users.filter((u) => u.isBlocked).length,
            admins: users.filter((u) => ['super_admin', 'superadmin'].includes(String(u.role || u.userRole || '').toLowerCase())).length,
            attendees: users.filter((u) => u.role === 'attendee').length,
            new30: users.filter((u) => (toDate(u.createdAt)?.getTime() || 0) >= since).length,
        };
    }, [users]);

    const statCards = useMemo(() => [
        {
            label: 'Total users', value: stats.total, icon: <PeopleIcon  key="config-1510-0-2" />, accent: palette.rust,
            delta: stats.new30 > 0 ? { label: `+${compact(stats.new30)}`, trend: 'up' } : null,
            hint: stats.new30 > 0 ? `${compact(stats.new30)} joined in 30d` : 'Directory total',
        },
        {
            label: 'Active', value: stats.active, icon: <VerifiedIcon  key="config-1515-1-2" />, accent: palette.pine,
            hint: stats.total ? `${Math.round((stats.active / stats.total) * 100)}% of directory` : 'No users yet',
        },
        {
            label: 'Blocked', value: stats.blocked, icon: <DoNotDisturbIcon  key="config-1519-2-2" />, accent: palette.danger,
            hint: stats.blocked ? 'Access revoked' : 'Nobody blocked',
        },
        {
            label: 'Admins', value: stats.admins, icon: <ShieldIcon  key="config-1523-3-2" />, accent: palette.plum,
            hint: 'Elevated privileges',
        },
        {
            label: 'Attendees', value: stats.attendees, icon: <GroupsIcon  key="config-1527-4-2" />, accent: palette.amber,
            hint: 'Standard access',
        },
    ], [stats]);

    /* ── mutations ─────────────────────────────────────────────────────────── */

    const isSelf = useCallback((u) => Boolean(myUid) && u?.id === myUid, [myUid]);

    const confirmDelete = async () => {
        const target = confirm.user;
        if (!target) return;
        if (isSelf(target)) { toast.error('You cannot delete your own account'); return; }

        setBusy(true);
        try {
            await updateDoc(doc(db, 'users', target.id), {
                isActive: false,
                isBlocked: true,
                deactivatedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, isActive: false, isBlocked: true } : u)));
            toast.success('User deactivated and access blocked');
            setConfirm({ open: false, kind: null, user: null });
        } catch (err) {
            console.error('[users] delete failed:', err);
            toast.error(err.code === 'permission-denied' ? 'You do not have permission to deactivate users' : 'Failed to deactivate user');
        } finally {
            setBusy(false);
        }
    };

    const confirmBlock = async () => {
        const target = confirm.user;
        if (!target) return;
        if (isSelf(target)) { toast.error('You cannot block your own account'); return; }

        const nextBlocked = !target.isBlocked;
        setBusy(true);
        try {
            await updateDoc(doc(db, 'users', target.id), { isBlocked: nextBlocked, updatedAt: serverTimestamp() });
            // optimistic local patch — no full refetch needed
            setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, isBlocked: nextBlocked } : u)));
            toast.success(nextBlocked ? 'User blocked' : 'User unblocked');
            setConfirm({ open: false, kind: null, user: null });
        } catch (err) {
            console.error('[users] block failed:', err);
            toast.error('Failed to update user status');
        } finally {
            setBusy(false);
        }
    };

    const verifyEmailManually = async (target) => {
        if (!target?.id || busy) return;
        setBusy(true);
        try {
            await manualVerifyUserEmail(target.id, true);
            setUsers((prev) => prev.map((u) => u.id === target.id ? { ...u, isVerified: true, emailVerified: true } : u));
            toast.success(`${target.email || 'User'} verified manually`);
        } catch (err) {
            console.error('[users] manual verification failed:', err);
            toast.error(friendlyError(err, 'Unable to verify this account.'));
        } finally {
            setBusy(false);
        }
    };

    const resendVerification = async (target) => {
        if (!target?.id || busy) return;
        setBusy(true);
        try {
            const result = await sendProfessionalVerificationEmail(target.id);
            if (result?.requiresUserSession) {
                toast('Firebase verification can be resent by the user from their TELCOFAIR verification screen.');
            } else if (result?.manualVerificationRequired) {
                toast('Manual verification is selected. Use Verify Manually for this account.');
            } else {
                toast.success(result?.provider === 'whatsapp' ? `WhatsApp verification requested for ${target.email || 'user'}` : `Verification message sent to ${target.email || 'user'}`);
            }
        } catch (err) {
            console.error('[users] verification email failed:', err);
            toast.error(friendlyError(err, 'Unable to send verification instructions.'));
        } finally {
            setBusy(false);
        }
    };

    const filtersActive = Boolean(search || roleFilter || statusFilter);
    const clearFilters = () => { setSearch(''); setRoleFilter(''); setStatusFilter(''); setPage(0); };

    const headCellSx = {
        borderBottom: `1px solid ${palette.hairline}`,
        fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
        letterSpacing: '0.12em', textTransform: 'uppercase', color: palette.inkLight,
        background: 'transparent', py: 1.5,
    };
    const bodyCellSx = { borderBottom: `1px solid ${palette.hairlineSoft}`, py: 1.35 };

    /* ═══════════════════════════════════════════════════════════════════════
       RENDER
       ═══════════════════════════════════════════════════════════════════════ */

    return (
        <Box
            sx={{
                minHeight: '100vh', position: 'relative', fontFamily: fontBody, color: palette.ink,
                background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
                '&::before': {
                    content: '""', position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.4,
                    backgroundImage:
                        'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
                    animation: 'tfGrain 8s steps(6) infinite',
                },
            }}
        >
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            <Box sx={{ position: 'relative', zIndex: 2 }}></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 3, md: 4 } }}
            >
                {/* ── hero ─────────────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: fontMono, fontSize: '0.64rem', fontWeight: 500,
                                    letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.rust, mb: 0.75,
                                }}
                            >
                                TelcoFair · Administration
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay, fontWeight: 600,
                                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                                    letterSpacing: '-0.035em', lineHeight: 1.05, color: palette.ink,
                                }}
                            >
                                User{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        fontStyle: 'italic',
                                        background: `linear-gradient(120deg, ${palette.rust}, ${palette.amber})`,
                                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    management
                                </Box>
                            </Typography>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.92rem', mt: 0.75, maxWidth: 540 }}>
                                Everyone with an account on the platform — roles, access and verification in one place.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.75, flexWrap: 'wrap' }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                        borderRadius: '999px', border: `1px solid ${rgba(palette.pine, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.pine, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <LiveIcon sx={{ fontSize: 9, color: palette.pine, animation: 'tfPulse 2s ease-in-out infinite' }} />
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.pine }}>
                                        {compact(stats.active)} ACTIVE
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                        borderRadius: '999px', border: `1px solid ${rgba(palette.plum, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.plum, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <ShieldIcon sx={{ fontSize: 12, color: palette.plum }} />
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.plum }}>
                                        {compact(stats.admins)} ADMINS
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Button
                                onClick={() => loadUsers(true)}
                                disabled={refreshing}
                                startIcon={<RefreshIcon sx={{ animation: refreshing ? 'tfSpin 1s linear infinite' : 'none' }} />}
                                sx={{
                                    px: 2.25, py: 1, borderRadius: '14px', textTransform: 'none', fontWeight: 600,
                                    fontSize: '0.82rem', color: palette.inkSoft,
                                    border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.8)}, ${rgba('#FFFFFF', 0.45)})`,
                                    backdropFilter: 'blur(14px)',
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.9)}, 0 8px 20px -12px ${rgba(palette.ink, 0.5)}`,
                                    '&:hover': { color: palette.ink, background: rgba('#FFFFFF', 0.9) },
                                }}
                            >
                                {refreshing ? 'Refreshing' : 'Refresh'}
                            </Button>
                            <Button
                                onClick={() => setFormState({ open: true, mode: 'add', user: null })}
                                startIcon={<AddIcon />}
                                sx={{
                                    px: 2.5, py: 1, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
                                    fontSize: '0.82rem', color: '#fff',
                                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                    border: `1px solid ${rgba('#FFFFFF', 0.25)}`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.35)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                                }}
                            >
                                Add user
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── stats ────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 2, mb: 3,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                    }}
                >
                    {statCards.map((s, i) => (
                        <StatCard key={s.label} {...s} delay={i * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* ── filter bar ───────────────────────────────────────────── */}
                <GlassPanel accent={palette.rust} delay={0.1} sx={{ mb: 2.5 }} padding={{ xs: 2, md: 2.25 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Search name, email, company or phone…"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            sx={{ flex: '1 1 280px', minWidth: 220, ...glassField(palette.rust) }}
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: palette.inkFaint }} />
                                    </InputAdornment>
                                ),
                            } }}
                        />
                        <FormControl size="small" sx={{ minWidth: 160, ...glassField(palette.rust) }}>
                            <InputLabel>Role</InputLabel>
                            <Select value={roleFilter} label="Role" onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}>
                                <MenuItem value="">All roles</MenuItem>
                                {ROLES.map((r) => (
                                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 160, ...glassField(palette.rust) }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                                <MenuItem value="">All statuses</MenuItem>
                                {Object.entries(STATUS_META).map(([key, meta]) => (
                                    <MenuItem key={key} value={key}>{meta.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <AnimatePresence initial={false}>
                            {filtersActive && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <Button
                                        onClick={clearFilters}
                                        startIcon={<FilterOffIcon sx={{ fontSize: 17 }} />}
                                        sx={{
                                            textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 1.75,
                                            color: palette.rust, border: `1px solid ${rgba(palette.rust, 0.3)}`,
                                            background: rgba(palette.rust, 0.06),
                                            '&:hover': { background: rgba(palette.rust, 0.12) },
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Box
                            sx={{
                                ml: { xs: 0, md: 'auto' }, px: 1.25, py: 0.6, borderRadius: '12px',
                                fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.inkSoft,
                                background: rgba(palette.ink, 0.04), border: `1px solid ${palette.hairlineSoft}`,
                            }}
                        >
                            {compact(filtered.length)} / {compact(users.length)}
                        </Box>
                    </Box>
                </GlassPanel>

                {/* ── directory ────────────────────────────────────────────── */}
                <GlassPanel accent={palette.pine} delay={0.15} padding={{ xs: 1.75, md: 2.5 }}>
                    <PanelHead
                        icon={<PeopleIcon />}
                        title="Users directory"
                        subtitle={`${compact(filtered.length)} ${filtered.length === 1 ? 'user' : 'users'} matching your view`}
                        accent={palette.pine}
                        right={
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                {ROLES.slice(0, 3).map((r) => {
                                    const n = users.filter((u) => u.role === r.value).length;
                                    if (!n) return null;
                                    return (
                                        <Box
                                            key={r.value}
                                            sx={{
                                                px: 1, py: 0.35, borderRadius: '9px', fontFamily: fontMono,
                                                fontSize: '0.6rem', fontWeight: 600, color: r.accent,
                                                bgcolor: rgba(r.accent, 0.1), border: `1px solid ${rgba(r.accent, 0.18)}`,
                                            }}
                                        >
                                            {r.label} · {n}
                                        </Box>
                                    );
                                })}
                            </Box>
                        }
                    />

                    <TableContainer sx={{ mx: -0.5, px: 0.5 }}>
                        <Table sx={{ minWidth: 720 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>User</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>Contact</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>Company</TableCell>
                                    <TableCell sx={headCellSx}>Role</TableCell>
                                    <TableCell sx={headCellSx}>Status</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', xl: 'table-cell' } }}>Joined</TableCell>
                                    <TableCell align="right" sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={7} sx={{ border: 0, py: 0.75 }}>
                                                <Skeleton variant="rounded" height={52} sx={{ borderRadius: '14px', bgcolor: palette.hairline }} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : paginated.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ border: 0 }}>
                                            {users.length === 0 ? (
                                                <EmptyState
                                                    icon={<PeopleIcon />}
                                                    title="No users yet"
                                                    text="Create the first account to get the directory started."
                                                    action="Add your first user"
                                                    onAction={() => setFormState({ open: true, mode: 'add', user: null })}
                                                />
                                            ) : (
                                                <EmptyState
                                                    icon={<SearchIcon />}
                                                    title="Nothing matches"
                                                    text="Try a different search term or clear the filters."
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginated.map((u, i) => {
                                        const self = isSelf(u);
                                        const accent = roleMeta(u.role).accent;
                                        return (
                                            <TableRow
                                                key={u.id}
                                                sx={{
                                                    opacity: 0,
                                                    animation: 'tfRowIn .45s cubic-bezier(.22,1,.36,1) forwards',
                                                    animationDelay: `${Math.min(i, 10) * 40}ms`,
                                                    transition: 'background .3s ease, box-shadow .3s ease',
                                                    '&:hover': {
                                                        background: `linear-gradient(90deg, ${rgba(accent, 0.07)}, transparent 70%)`,
                                                        boxShadow: `inset 3px 0 0 ${accent}`,
                                                    },
                                                    '&:hover .tf-actions': { opacity: 1 },
                                                }}
                                            >
                                                <TableCell sx={bodyCellSx}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                                        <UserAvatar user={u} size={38} radius={13} />
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                                    {u.displayName || u.email?.split('@')[0] || 'Unknown'}
                                                                </Typography>
                                                                {u.isVerified && <VerifiedIcon sx={{ fontSize: 14, color: palette.pine }} />}
                                                                {self && (
                                                                    <Box
                                                                        sx={{
                                                                            px: 0.7, py: 0.15, borderRadius: '6px', fontFamily: fontMono,
                                                                            fontSize: '0.53rem', fontWeight: 700, textTransform: 'uppercase',
                                                                            color: palette.rust, bgcolor: rgba(palette.rust, 0.1),
                                                                        }}
                                                                    >
                                                                        You
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            <Typography sx={{ fontSize: '0.72rem', color: palette.inkLight }} noWrap>
                                                                {u.email || '—'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
                                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.75rem', color: palette.inkSoft }}>
                                                        {u.phoneNumber || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', md: 'table-cell' } }}>
                                                    <Typography sx={{ fontSize: '0.8rem', color: palette.inkSoft }} noWrap>
                                                        {u.companyName || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}><RoleBadge role={u.role} /></TableCell>
                                                <TableCell sx={bodyCellSx}><StatusPill status={statusOf(u)} /></TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
                                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.72rem', color: palette.inkLight }}>
                                                        {formatDate(u.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={bodyCellSx}>
                                                    <Box
                                                        className="tf-actions"
                                                        sx={{
                                                            display: 'flex', gap: 0.4, justifyContent: 'flex-end',
                                                            opacity: { xs: 1, md: 0.45 }, transition: 'opacity .3s ease',
                                                        }}
                                                    >
                                                        {[
                                                            {
                                                                key: 'view', title: 'View details', tone: palette.teal,
                                                                icon: <VisibilityIcon sx={{ fontSize: 17 }}  key="config-1984-0-3" />,
                                                                onClick: () => setDetail({ open: true, user: u }),
                                                            },
                                                            {
                                                                key: 'edit', title: 'Edit', tone: palette.amber,
                                                                icon: <EditIcon sx={{ fontSize: 17 }}  key="config-1989-1-3" />,
                                                                onClick: () => setFormState({ open: true, mode: 'edit', user: u }),
                                                            },
                                                            ...(!(u.isVerified === true || u.emailVerified === true) ? [{
                                                                key: 'verify-email', title: 'Verify email manually', tone: palette.pine,
                                                                icon: <VerifiedIcon sx={{ fontSize: 17 }}  key="config-1994-0-3" />,
                                                                disabled: busy,
                                                                onClick: () => verifyEmailManually(u),
                                                            }, {
                                                                key: 'resend-verification', title: 'Resend professional verification email', tone: palette.teal,
                                                                icon: <EmailIcon sx={{ fontSize: 17 }}  key="config-1999-1-3" />,
                                                                disabled: busy,
                                                                onClick: () => resendVerification(u),
                                                            }] : []),
                                                            {
                                                                key: 'block',
                                                                title: self ? 'You cannot block yourself' : u.isBlocked ? 'Unblock' : 'Block',
                                                                tone: u.isBlocked ? palette.pine : palette.warning,
                                                                icon: u.isBlocked ? <CheckCircleIcon sx={{ fontSize: 17 }} /> : <BlockIcon sx={{ fontSize: 17 }} />,
                                                                disabled: self,
                                                                onClick: () => setConfirm({ open: true, kind: 'block', user: u }),
                                                            },
                                                            {
                                                                key: 'delete',
                                                                title: self ? 'You cannot deactivate yourself' : 'Deactivate',
                                                                tone: palette.danger,
                                                                icon: <DeleteIcon sx={{ fontSize: 17 }}  key="config-2013-4-3" />,
                                                                disabled: self,
                                                                onClick: () => setConfirm({ open: true, kind: 'delete', user: u }),
                                                            },
                                                        ].map((action) => (
                                                            <Tooltip key={action.key} title={action.title} arrow>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        disabled={action.disabled}
                                                                        onClick={action.onClick}
                                                                        sx={{
                                                                            width: 30, height: 30, borderRadius: '10px', color: action.tone,
                                                                            border: `1px solid ${rgba(action.tone, 0.18)}`,
                                                                            background: rgba(action.tone, 0.06),
                                                                            transition: 'transform .25s ease, background .25s ease',
                                                                            '&:hover': { background: rgba(action.tone, 0.16), transform: 'translateY(-2px)' },
                                                                            '&.Mui-disabled': { color: palette.inkFaint, borderColor: palette.hairlineSoft, background: 'transparent' },
                                                                        }}
                                                                    >
                                                                        {action.icon}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        count={filtered.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, next) => setPage(next)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{
                            mt: 1, borderTop: `1px solid ${palette.hairlineSoft}`,
                            '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: 0 },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontFamily: fontMono, fontSize: '0.72rem', color: palette.inkLight,
                            },
                        }}
                    />
                </GlassPanel>
            </Container>

            {/* ── dialogs ──────────────────────────────────────────────────── */}
            <UserFormDialog
                open={formState.open}
                mode={formState.mode}
                user={formState.user}
                companies={companies}
                onClose={() => setFormState((s) => ({ ...s, open: false }))}
                onSaved={() => loadUsers(true)}
            />

            <UserDetailDialog
                user={detail.user}
                open={detail.open}
                onClose={() => setDetail((s) => ({ ...s, open: false }))}
                onEdit={(u) => setFormState({ open: true, mode: 'edit', user: u })}
                isSelf={isSelf(detail.user)}
            />

            <ConfirmDialog
                open={confirm.open && confirm.kind === 'delete'}
                busy={busy}
                tone={palette.danger}
                icon={<DeleteIcon />}
                title="Deactivate this user?"
                body={`This preserves ${confirm.user?.displayName || confirm.user?.email || 'this user'} for history, but blocks access and marks the account inactive.`}
                confirmLabel="Deactivate"
                onClose={() => setConfirm({ open: false, kind: null, user: null })}
                onConfirm={confirmDelete}
            />

            <ConfirmDialog
                open={confirm.open && confirm.kind === 'block'}
                busy={busy}
                tone={confirm.user?.isBlocked ? palette.pine : palette.warning}
                icon={confirm.user?.isBlocked ? <CheckCircleIcon /> : <BlockIcon />}
                title={confirm.user?.isBlocked ? 'Unblock this user?' : 'Block this user?'}
                body={
                    confirm.user?.isBlocked
                        ? `${confirm.user?.displayName || 'They'} will regain access to the platform immediately.`
                        : `${confirm.user?.displayName || 'They'} will be locked out of the platform until unblocked.`
                }
                confirmLabel={confirm.user?.isBlocked ? 'Unblock' : 'Block'}
                onClose={() => setConfirm({ open: false, kind: null, user: null })}
                onConfirm={confirmBlock}
            />

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfRule {
                    0%, 100% { opacity: .7; transform: scaleX(1); }
                    50%      { opacity: 1;  transform: scaleX(1.35); }
                }
                @keyframes tfSpin { to { transform: rotate(360deg); } }
                @keyframes tfRowIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: ${rgba(palette.ink, 0.14)}; border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default Users;